/**
 * Normalises whatever a bundler hands back for a `require()`d static asset
 * into a URL string.
 *
 * The design system's web renderers ship under more than one bundler, and
 * each returns a different module shape for the same `require('./x.svg')`:
 *
 * - Vite / Vitest: the asset URL as a plain string.
 * - CommonJS interop over an ESM asset module: `{ default: '<url>' }`.
 * - Next.js (webpack) static image loader: `{ default: { src, height, width } }`
 *   (or the object itself when interop already unwrapped `default`).
 *
 * Anything else is a wiring mistake — a missing loader, a mocked module with
 * the wrong shape — so it throws rather than letting `[object Object]` or
 * `undefined` reach an `<img src>`.
 */
export function resolveAssetModule(mod: unknown): string {
  if (typeof mod === 'string') return mod;
  if (isRecord(mod)) {
    const inner = 'default' in mod ? mod.default : mod;
    if (typeof inner === 'string') return inner;
    if (isRecord(inner) && typeof inner.src === 'string') return inner.src;
    throw new Error(
      `resolveAssetModule: unsupported asset module shape (${'default' in mod ? `default: ${describe(inner)}` : describe(mod)})`,
    );
  }
  throw new Error(`resolveAssetModule: unsupported asset module shape (${describe(mod)})`);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function describe(value: unknown): string {
  if (value === null) return 'null';
  if (typeof value !== 'object') return typeof value;
  const keys = Object.keys(value);
  return keys.length === 0 ? 'empty object' : `object with keys ${keys.join(', ')}`;
}
